import * as Sentry from "@sentry/react";
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import { shouldSkipSentryForApiError } from "./sentryApiErrorFilter";
import { buildSampledApiClientErrorPayload } from "@/utils/pulseTelemetry";

/**
 * Axios merges baseURL with request paths like `/admin/...` by replacing the
 * pathname of baseURL. If VITE_API_BASE_URL is `http://localhost:8001` (root
 * only), the merged URL becomes `http://localhost:8001/admin/...` instead of
 * `http://localhost:8001/api/v1/admin/...`, and FastAPI responds with 404.
 */
function ensureApiV1OnRootOnlyBase(url: string): string {
  if (!url || url.startsWith("/")) {
    return url;
  }
  try {
    const u = new URL(url);
    const path = u.pathname.replace(/\/+$/, "") || "/";
    if (path !== "/") {
      return url;
    }
    u.pathname = "/api/v1";
    return u.toString().replace(/\/+$/, "");
  } catch {
    return url;
  }
}

// Function to get the appropriate API base URL
const getApiBaseUrl = (): string => {
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;

  if (!envApiUrl) {
    throw new Error("VITE_API_BASE_URL environment variable is not set");
  }

  // If it's a relative URL (starts with /), use it as-is (for proxy setup)
  if (envApiUrl.startsWith('/')) {
    return envApiUrl;
  }

  // If we're in a browser environment, check for special PDF export case
  if (typeof window !== "undefined") {
    // Local dev: when .env points at localhost:8002 but Vite runs on :3002, use the
    // dev-server proxy (/api → VITE_DEV_API_PROXY) to avoid cross-origin failures.
    try {
      const apiUrl = new URL(envApiUrl, window.location.origin);
      const localHosts = new Set(['localhost', '127.0.0.1']);
      if (
        localHosts.has(apiUrl.hostname) &&
        localHosts.has(window.location.hostname) &&
        apiUrl.hostname === window.location.hostname &&
        apiUrl.port &&
        window.location.port &&
        apiUrl.port !== window.location.port
      ) {
        return '/api/v1/';
      }
    } catch {
      // continue with configured URL
    }

    // If current page is accessed via host.docker.internal (Docker-based PDF generation)
    // Use the Vite proxy by returning a relative URL
    if (window.location.hostname === 'host.docker.internal') {
      // Return relative URL to use Vite's proxy configuration
      // This way Docker container -> host.docker.internal:3001/api -> proxy -> localhost:8001
      return '/api/v1';
    }

    // Check if we're on the /pdf route with an export query param
    const isPdfRoute = window.location.pathname === "/pdf";
    if (isPdfRoute) {
      const searchParams = new URLSearchParams(window.location.search);
      const exportParam = searchParams.get("export");

      // If export param is present with value 'pdf' or 'png', use internal cluster URL
      if (exportParam === "pdf" || exportParam === "png") {
        const clusterApiUrl = "http://ani-api.ani-api:8001/api/v1/";
        return clusterApiUrl;
      }
    }

    // If it's an absolute URL, handle protocol conversion if needed
    const currentProtocol = window.location.protocol; // 'https:' or 'http:'
    const isHttps = currentProtocol === "https:";

    // Parse the environment URL and adjust protocol if needed
    try {
      const url = new URL(envApiUrl);

      // Special case: Don't convert localhost HTTP to HTTPS even if page is HTTPS
      // This allows HTTPS frontend (via ngrok) to talk to HTTP localhost backend
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        return ensureApiV1OnRootOnlyBase(envApiUrl);
      }

      if (isHttps && url.protocol === "http:") {
        // Convert HTTP to HTTPS if current page is HTTPS (but not for localhost)
        url.protocol = "https:";
        return ensureApiV1OnRootOnlyBase(url.toString());
      } else if (!isHttps && url.protocol === "https:") {
        // Convert HTTPS to HTTP if current page is HTTP (for local development)
        url.protocol = "http:";
        return ensureApiV1OnRootOnlyBase(url.toString());
      }
    } catch {
      // Failed to parse URL, continue with original
    }
  }

  // Return original URL if we can't detect protocol or no conversion needed
  return ensureApiV1OnRootOnlyBase(envApiUrl);
};

// Use dynamic API base URL
const API_BASE_URL = getApiBaseUrl();

/** Absolute or root-relative URL for browser navigation (``<a href>``, ``window.open``) to API routes. */
export function browserApiUrl(apiPath: string): string {
  const path = apiPath.startsWith("/") ? apiPath : `/${apiPath}`;
  const base = API_BASE_URL;
  if (base.startsWith("http://") || base.startsWith("https://")) {
    const root = base.endsWith("/") ? base : `${base}/`;
    return new URL(path.replace(/^\//, ""), root).toString();
  }
  const basePath = (base.startsWith("/") ? base : `/${base}`).replace(/\/$/, "");
  return `${basePath}${path}`;
}

function shouldSendDeploymentRoutingHeaders(): boolean {
  if (typeof window === "undefined") {
    return true;
  }
  try {
    const apiUrl = new URL(API_BASE_URL, window.location.origin);
    return !["localhost", "127.0.0.1", "host.docker.internal"].includes(
      apiUrl.hostname
    );
  } catch {
    return true;
  }
}

// Safe localStorage access
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

// Safe localStorage remove
const safeRemoveItem = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    // Silently ignore - localStorage may not be available (SSR/iframe restrictions)
  }
};

// Flag to prevent multiple 401 redirects (avoids infinite loop)
let isRedirectingToLogin = false;

/**
 * Reset the redirect flag - call this when user successfully lands on login page
 * This ensures the flag is cleared for a fresh login attempt
 */
export const resetLoginRedirectFlag = (): void => {
  isRedirectingToLogin = false;
};

// Create a configured axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Essential for session cookies
  headers: {
    "Content-Type": "application/json",
  },
});

// Import context tracker
import { contextTrackingRequestInterceptor, contextTrackingResponseInterceptor } from "./contextTracker";
import { getDeploymentRoutingHeaders } from "@/utils/deploymentRouting";
import { getStoredProgenyEnvironment } from "@/context/ProgenyEnvironmentContext";

// Request interceptor for hybrid authentication (session cookies OR Bearer token)
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Always include credentials for session cookies
    config.withCredentials = true;

    const routingHeaders = shouldSendDeploymentRoutingHeaders()
      ? getDeploymentRoutingHeaders()
      : {};
    Object.assign(config.headers, routingHeaders);

    // Check for Bearer token (for non-SSO users)
    const authDataStr = safeGetItem("auth_data");
    if (authDataStr) {
      try {
        const authData = JSON.parse(authDataStr);
        if (authData.auth_token) {
          // Non-SSO user: Use Bearer token
          config.headers.Authorization = `Bearer ${authData.auth_token}`;
        }
      } catch (_error) {
        // Invalid auth data, ignore
      }
    }
    // If no Bearer token, session cookies will be used automatically (SSO users)

    // Add Progeny environment header for Progeny API requests
    // This ensures consistent environment targeting across multiple API pods
    if (config.url?.includes("/progeny")) {
      const progenyEnv = getStoredProgenyEnvironment();
      config.headers["X-Progeny-Environment"] = progenyEnv;
    }

    // FormData must not use a bare multipart Content-Type (no boundary) — let the
    // runtime set ``multipart/form-data; boundary=...`` or uploads arrive empty.
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      config.headers.delete("Content-Type");
    }

    // Track API request for ANI context
    contextTrackingRequestInterceptor(config);

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for consistent error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Track successful API response for ANI context
    contextTrackingResponseInterceptor(response);
    
    return response;
  },
  (error: AxiosError) => {
    // Handle common error cases
    if (error.response) {
      // Server responded with non-2xx status

      // Capture API errors in Sentry (except expected auth / optional-resource outcomes)
      if (error.response.status !== 401 && !shouldSkipSentryForApiError(error)) {
        Sentry.captureException(error, {
          tags: {
            api_status: error.response.status,
            api_url: error.config?.url,
          },
          extra: {
            response_data: error.response.data,
          },
        });
      }

      // Handle 401 Unauthorized - session/token expired, redirect to login
      // Skip redirect on /pdf — SSO preview and PDF export handle auth separately
      const pathname =
        typeof window !== "undefined" ? window.location.pathname : "";
      const isPdfRoute = pathname === "/pdf";
      const isPublicBookingRoute =
        pathname.startsWith("/book/") ||
        Boolean(error.config?.url?.includes("/booking-link"));

      if (
        error.response.status === 401 &&
        !error.config?.url?.includes("/auth") &&
        !isPdfRoute &&
        !isPublicBookingRoute
      ) {
        // Prevent multiple redirects (avoids infinite loop when multiple API calls fail)
        if (!isRedirectingToLogin) {
          isRedirectingToLogin = true;
          
          // Clear both WorkOS user data and Bearer token data
          safeRemoveItem("workos_user");
          safeRemoveItem("auth_data");

          // Redirect to login page
          if (typeof window !== "undefined") {
            // Use replace to prevent back button issues
            window.location.replace("/login");
          }
        }
        
        // Return a never-resolving promise to stop further processing
        // This prevents the error from propagating and causing re-renders
        return new Promise(() => {});
      }
    } else if (error.request) {
      // Network error - capture in Sentry
      Sentry.captureException(error, {
        tags: {
          error_type: "network_error",
          api_url: error.config?.url,
        },
      });
    }

    const pulsePayload = buildSampledApiClientErrorPayload(
      error,
      typeof window !== "undefined" ? window.location.pathname : undefined,
    );
    if (pulsePayload) {
      void import("./user").then(({ recordPulseApiClientError }) => {
        recordPulseApiClientError(pulsePayload).catch(() => {});
      });
    }

    return Promise.reject(error);
  }
);

// Export the configured axios instance
export default api;

// Export a simple request function for direct use
export const apiRequest = async <T = any>(
  config: AxiosRequestConfig
): Promise<T> => {
  const response = await api.request<T>(config);
  return response.data;
};

// Convenience methods with Sentry tracing
export const get = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return Sentry.startSpan(
    { op: "http.client", name: `GET ${url}` },
    async (span) => {
      const response = await api.get<T>(url, config);
      span.setAttribute("http.status_code", response.status);
      return response.data;
    }
  );
};

export const post = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return Sentry.startSpan(
    { op: "http.client", name: `POST ${url}` },
    async (span) => {
      const response = await api.post<T>(url, data, config);
      span.setAttribute("http.status_code", response.status);
      return response.data;
    }
  );
};

export const put = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return Sentry.startSpan(
    { op: "http.client", name: `PUT ${url}` },
    async (span) => {
      const response = await api.put<T>(url, data, config);
      span.setAttribute("http.status_code", response.status);
      return response.data;
    }
  );
};

export const patch = async <T = any>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> => {
  return Sentry.startSpan(
    { op: "http.client", name: `PATCH ${url}` },
    async (span) => {
      const response = await api.patch<T>(url, data, config);
      span.setAttribute("http.status_code", response.status);
      return response.data;
    }
  );
};

export const del = async <T = any>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> => {
  return Sentry.startSpan(
    { op: "http.client", name: `DELETE ${url}` },
    async (span) => {
      const response = await api.delete<T>(url, config);
      span.setAttribute("http.status_code", response.status);
      return response.data;
    }
  );
};

// Specialized function for file downloads that returns full response with headers
export const apiRequestWithHeaders = async <T = any>(
  config: AxiosRequestConfig
): Promise<AxiosResponse<T>> => {
  const response = await api.request<T>(config);
  return response;
}

// Register axios instance with PDF tracker if available
const registerWithPDFTracker = (): boolean => {
  if (typeof window !== "undefined" && (window as any).__PDF_REGISTER_AXIOS__) {
    (window as any).__PDF_REGISTER_AXIOS__(api);
    // Set a flag to confirm registration for XHR fallback logic
    (window as any).__PDF_AXIOS_REGISTERED__ = true;
    return true;
  }
  return false;
};

// Retry registration with exponential backoff
const registerWithRetry = (attempt: number = 0, maxAttempts: number = 5) => {
  if (registerWithPDFTracker()) {
    return; // Successfully registered
  }
  
  if (attempt < maxAttempts) {
    // Exponential backoff: 100ms, 200ms, 400ms, 800ms, 1600ms
    const delay = 100 * Math.pow(2, attempt);
    setTimeout(() => {
      registerWithRetry(attempt + 1, maxAttempts);
    }, delay);
  }
};

// Try to register with retry logic
if (typeof window !== "undefined") {
  registerWithRetry();
}
